import { CloudRain, Trees, Waves, Wind, Bell, Droplets, Play, Pause, VolumeX, Volume1 } from "lucide-react";
import { Card } from "./Card";

const ambientIcons = {
  rain: CloudRain,
  forest: Trees,
  ocean: Waves,
  wind: Wind,
  chimes: Bell,
  stream: Droplets
};

const ambientLabels = {
  rain: "Rain",
  forest: "Forest",
  ocean: "Ocean",
  wind: "Wind",
  chimes: "Chimes",
  stream: "Stream"
};

export const AmbientSoundMixer = ({
  ambientSounds,
  ambientRef,
  activeAmbient,
  ambientVolume,
  setAmbientVolume,
  ambientPlaying,
  onSelectSound,
  onTogglePlayback
}) => {
  const soundKeys = Object.keys(ambientSounds);
  
  if (soundKeys.length === 0) return null;

  return (
    <section className="space-y-4 animate-fade-in-up stagger-1">
      <p className="text-xs uppercase tracking-[0.15em] text-[#6B7280] font-medium">
        Ambient Sounds
      </p>
      <Card className="p-6">
        <audio ref={ambientRef} loop preload="auto" />
        
        {/* Sound Selection Grid */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {soundKeys.map((key) => {
            const Icon = ambientIcons[key] || Waves;
            const isActive = activeAmbient === key;
            return (
              <button
                key={key}
                onClick={() => onSelectSound(key, ambientSounds[key]?.url)}
                className={`
                  flex flex-col items-center gap-2.5 p-4 rounded-2xl
                  transition-all duration-300 ease-out
                  ${isActive 
                    ? "bg-[#4A6741] text-white shadow-lg shadow-[#4A6741]/20 scale-[1.02]" 
                    : "bg-[#F9FAFB] text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#4A6741]"
                  }
                `}
                data-testid={`ambient-${key}`}
              >
                <Icon size={26} strokeWidth={1.5} />
                <span className="text-xs font-medium tracking-wide">
                  {ambientLabels[key] || key}
                </span>
              </button>
            );
          })}
        </div>

        {/* Controls - Only show when sound is selected */}
        {activeAmbient && (
          <div className="space-y-4 pt-5 border-t border-[#E5E7EB] animate-fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={onTogglePlayback}
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center
                    transition-all duration-300 ease-out shadow-md
                    ${ambientPlaying 
                      ? "bg-[#D4A373] hover:bg-[#C49565]" 
                      : "bg-[#4A6741] hover:bg-[#5A7D4F]"
                    }
                  `}
                  data-testid="ambient-play-btn"
                >
                  {ambientPlaying ? (
                    <Pause size={20} className="text-white" />
                  ) : (
                    <Play size={20} className="text-white ml-0.5" />
                  )}
                </button>
                <span className="text-[#1F2937] font-medium">
                  {ambientLabels[activeAmbient] || activeAmbient}
                </span>
              </div>
              <div className={`
                p-2 rounded-full transition-colors duration-300
                ${ambientPlaying ? "bg-[#4A6741]/10" : "bg-[#F3F4F6]"}
              `}>
                {ambientPlaying ? (
                  <Volume1 size={18} className="text-[#4A6741]" />
                ) : (
                  <VolumeX size={18} className="text-[#9CA3AF]" />
                )}
              </div>
            </div>
            
            {/* Volume Slider */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#6B7280] font-medium w-14">Volume</span>
              <input
                type="range"
                min="0"
                max="100"
                value={ambientVolume}
                onChange={(e) => setAmbientVolume(parseInt(e.target.value))}
                className="flex-1"
                data-testid="ambient-volume-slider"
              />
              <span className="text-sm text-[#6B7280] w-10 text-right">{ambientVolume}%</span>
            </div>
          </div>
        )}

        <p className="text-sm text-[#9CA3AF] text-center mt-5 pt-4 border-t border-[#F3F4F6]">
          Layer ambient sounds with your meditation
        </p>
      </Card>
    </section>
  );
};
